import {
  deleteV1CompaniesCompanyIdCompanyExportRenderersId,
  getV1CompaniesCompanyIdCompanyExportRenderers,
  postV1CompaniesCompanyIdCompanyExportRenderers,
  SettlementExportRendererKey,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { CompanyExportRenderer } from '~hooks/useCompanyExportRenderers/models';
import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

interface CreateCompanyExportRendererParams {
  companyId: string;
  rendererKey: SettlementExportRendererKey;
}

interface DeleteCompanyExportRendererParams {
  companyId: string;
  companyExportRendererId: string;
}

export default function useCompanyExportRenderers() {
  const fetchAllCompanyExportRenderers = useCallback(
    async (companyId: string, signal?: AbortSignal) => {
      try {
        return await fetchAllApiPages(
          (query, pageSignal) =>
            getV1CompaniesCompanyIdCompanyExportRenderers({
              path: { 'company-id': companyId },
              query,
              signal: pageSignal,
            }),
          CompanyExportRenderer.parse,
          { signal },
        );
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        return connection.handleRequestError(
          error,
          t('error_messages.company_export_renderers.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const createCompanyExportRenderer = useCallback(
    async ({ companyId, rendererKey }: CreateCompanyExportRendererParams) => {
      try {
        const response = await postV1CompaniesCompanyIdCompanyExportRenderers({
          path: { 'company-id': companyId },
          body: { renderer_key: rendererKey },
        });

        return CompanyExportRenderer.parse(response.data.data);
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.company_export_renderers.failed_to_enable'),
        );
      }
    },
    [],
  );

  const deleteCompanyExportRenderer = useCallback(
    async ({ companyId, companyExportRendererId }: DeleteCompanyExportRendererParams) => {
      try {
        await deleteV1CompaniesCompanyIdCompanyExportRenderersId({
          path: { 'company-id': companyId, id: companyExportRendererId },
        });
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.company_export_renderers.failed_to_disable'),
        );
      }
    },
    [],
  );

  return {
    createCompanyExportRenderer,
    deleteCompanyExportRenderer,
    fetchAllCompanyExportRenderers,
  };
}
